import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Button,
  Modal,
  Spin,
  Table,
  Input,
  Menu,
  Dropdown,
  Tag,
  Switch,
} from "antd";
import { useSelector } from "react-redux";
import { RiAddLine, RiFileList2Line, RiBookmark3Line } from "react-icons/ri";
import {
  getAllAiAppsPaginatedSearch,
  deleteAiAppById,
  updateAiAppApprovalById,
} from "@api/seo/aiApp";

const { Search } = Input;

const AiAppsList = ({ history }) => {
  const { user } = useSelector((state) => state.auth);
  const [aiApps, setAiApps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const page = parseInt(searchParams.get("page") || "1", 10);
    const pageSize = parseInt(searchParams.get("pageSize") || "10", 10);
    const search = searchParams.get("search") || "";

    setSearchText(search);
    setPagination({ current: page, pageSize, total: 0 });
    fetchAiApps(page, pageSize, search);
  }, []);

  const fetchAiApps = async (page, pageSize, searchText = "") => {
    setLoading(true);
    try {
      const res = await getAllAiAppsPaginatedSearch(page, pageSize, searchText);
      setAiApps(res.data.aiApps);
      setPagination((p) => ({
        ...p,
        total: res.data.paginationData.totalAiApps,
        current: res.data.paginationData.page,
        pageSize: res.data.paginationData.perPage,
      }));
    } catch (err) {
      console.error("Fetch aiApps error", err);
    } finally {
      setLoading(false);
    }
  };

  const handlePaginationChange = (page, pageSize) => {
    const searchParams = new URLSearchParams();
    searchParams.set("page", page);
    searchParams.set("pageSize", pageSize);
    if (searchText) searchParams.set("search", searchText);

    history.push({ search: searchParams.toString() });
    setPagination({ ...pagination, current: page, pageSize });
    fetchAiApps(page, pageSize, searchText);
  };

  const handleSearch = (searchValue) => {
    setSearchText(searchValue);
    setPagination({ ...pagination, current: 1 }); // Reset to first page
    fetchAiApps(1, pagination.pageSize, searchValue);

    const searchParams = new URLSearchParams();
    searchParams.set("page", "1");
    searchParams.set("pageSize", pagination.pageSize);
    if (searchValue) searchParams.set("search", searchValue);
    history.push({ search: searchParams.toString() });
  };

  // Function to handle ai-app update
  const handleAiAppUpdate = (id) => {
    // If user is superadmin, redirect to ai-app update page
    if (user?.role === "superadmin") {
      history.push(`/super-admin/seo/ai-app/${id}/update`);
    } else if (user?.role === "marketing") {
      history.push(`/marketing-admin/seo/ai-app/${id}/update`);
    }
  };

  // Function to handle ai-app delete
  const handleAiAppDelete = (id) => {
    Modal.confirm({
      title: "Are you sure you want to delete this ai-app?",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await deleteAiAppById(id);
          fetchAiApps(pagination.current, pagination.pageSize);
        } catch (error) {
          console.error("Error deleting ai-app", error);
        }
      },
    });
  };

  // Function to handle ai-app approval
  const handleAiAppApproval = async (checked, id) => {
    try {
      await updateAiAppApprovalById(id, { approved: checked });
      fetchAiApps(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error("Error updating ai-app approval", error);
    }
  };

  // Function to format date
  const formatDate = (date) => {
    if (!date) return "";
    const options = { day: "numeric", month: "short", year: "numeric" };
    return new Date(date).toLocaleDateString("en-US", options);
  };

  const menu = (
    <Menu>
      <Menu.Item
        key="1"
        icon={<RiFileList2Line />}
        onClick={() => {
          // If user is superadmin, redirect to ai-app create page
          if (user?.role === "superadmin") {
            history.push("/super-admin/seo/ai-app/create");
          } else if (user?.role === "marketing") {
            history.push("/marketing-admin/seo/ai-app/create");
          }
        }}
      >
        Create AI App
      </Menu.Item>
      <Menu.Item
        key="2"
        icon={<RiBookmark3Line />}
        onClick={() => {
          // If user is superadmin, redirect to ai-app categories page
          if (user?.role === "superadmin") {
            history.push("/super-admin/seo/ai-apps/ai-app-categories");
          } else if (user?.role === "marketing") {
            history.push("/marketing-admin/seo/ai-apps/ai-app-categories");
          }
        }}
      >
        AI App Categories
      </Menu.Item>
    </Menu>
  );

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (title) =>
        title.length > 25 ? `${title.substring(0, 25)}...` : title,
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      render: (category) => <Tag color="blue">{category?.name}</Tag>,
    },
    {
      title: "Published At",
      dataIndex: "publishedDate",
      key: "publishedDate",
      render: (date) => formatDate(date),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => formatDate(date),
    },
    {
      title: "Updated At",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (date) => formatDate(date),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <>
          <Button
            type="primary"
            size="small"
            style={{ marginRight: "15px" }}
            onClick={() => handleAiAppUpdate(record._id)}
          >
            Edit
          </Button>
          <Button
            type="danger"
            size="small"
            onClick={() => handleAiAppDelete(record._id)}
          >
            Delete
          </Button>
        </>
      ),
    },
    // Approved
    {
      title: "Approved",
      dataIndex: "approved",
      key: "approved",
      // Render a switch to toggle ai-app approval
      render: (approved, record) => (
        <Switch
          checked={approved}
          onChange={(checked) => handleAiAppApproval(checked, record._id)}
        />
      ),
    },
  ];

  return (
    <Card style={{ borderRadius: "0", height: "100vh", overflowY: "auto" }}>
      <Row justify="space-between" style={{ marginBottom: 16 }}>
        <Col>
          <h3>AI Apps List</h3>
        </Col>
        <Col>
          {/* AI Glossary Button */}
          <Button
            onClick={() => {
              if (user?.role === "superadmin") {
                history.push("/super-admin/seo/ai-glossary");
              } else if (user?.role === "marketing") {
                history.push("/marketing-admin/seo/ai-glossary");
              }
            }}
            style={{ marginRight: 10 }}
          >
            Open AI Glossary
          </Button>

          {/* AI Tools Button */}
          <Button
            onClick={() => {
              if (user?.role === "superadmin") {
                history.push("/super-admin/seo/ai-tools");
              } else if (user?.role === "marketing") {
                history.push("/marketing-admin/seo/ai-tools");
              }
            }}
            style={{ marginRight: 10 }}
          >
            Open AI Tools
          </Button>

          {/* Search */}
          <Search
            placeholder="Search by title/description"
            style={{ width: "300px", marginRight: 10 }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={handleSearch}
          />

          {/* AI App Options */}
          <Dropdown overlay={menu} placement="bottomRight">
            <Button icon={<RiAddLine />} style={{ marginLeft: "10px" }} />
          </Dropdown>
        </Col>
      </Row>
      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={aiApps}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
          }}
          loading={loading}
          onChange={({ current, pageSize }) =>
            handlePaginationChange(current, pageSize)
          }
          rowKey={(record) => record._id}
          scroll={{ y: "calc(100vh - 300px)" }}
        />
      </Spin>
    </Card>
  );
};

export default AiAppsList;
